import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useYoloModel, backendToProviders } from './hooks/useYoloModel';
import { renderToCanvas } from './lib/imageProcessing';
import {
  LABEL_PRESETS, DEFAULT_LABEL_PRESET_ID, getLabelPreset,
  DEFAULT_SKETCH_ENABLED, DEFAULT_SKETCH_CONTRAST, DEFAULT_SKETCH_SHADE, DEFAULT_REMOVE_BACKGROUND,
  renderPrintLabel, printLabelCanvas,
} from './lib/labelPrint';
import './PillowSegmentation.css';

// Reads/writes a small setting to localStorage, falling back silently
// (private browsing / disabled storage) so print settings persist across
// visits without ever breaking the page.
function readStoredValue(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : raw;
  } catch {
    return fallback;
  }
}

function writeStoredValue(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore — storage may be unavailable (private mode, quota, etc.)
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtMs(ms) {
  return ms < 1000 ? `${ms.toFixed(0)} ms` : `${(ms / 1000).toFixed(2)} s`;
}

function fmtTime(date) {
  if (!date) return '—';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function scoreColor(s) {
  if (s >= 0.80) return '#22c55e';
  if (s >= 0.65) return '#38bdf8';
  if (s >= 0.50) return '#fde68a';
  return '#f87171';
}

// function resizeTo224(canvas) {
//   const MAX = 224;
//   const scale = Math.min(MAX / canvas.width, MAX / canvas.height, 1);
//   const nw = Math.max(1, Math.round(canvas.width  * scale));
//   const nh = Math.max(1, Math.round(canvas.height * scale));
//   const out = document.createElement('canvas');
//   out.width = nw; out.height = nh;
//   out.getContext('2d').drawImage(canvas, 0, 0, nw, nh);
//   return out.toDataURL('image/png').replace(/^data:image\/png;base64,/, '');
// }

// function resizeTo224(canvas) {
//   const SIZE = 225;
//   const w = canvas.width;
//   const h = canvas.height;

//   // Both sides ≤ 224 — no resize (Python: return image, False)
//   if (w <= SIZE && h <= SIZE) {
//     return canvas.toDataURL('image/jpeg', 0.95).replace(/^data:image\/jpeg;base64,/, '');
//   }

//   // Scale so shortest side = 224 (Python: scale = SIZE / min(w, h))
//   const scale = SIZE / Math.min(w, h);
//   const nw = Math.round(w * scale);
//   const nh = Math.round(h * scale);

//   const tmp = document.createElement('canvas');
//   tmp.width = nw;
//   tmp.height = nh;
//   tmp.getContext('2d').drawImage(canvas, 0, 0, nw, nh);

//   // Center crop to 224×224 (Python: // → Math.floor)
//   const left = Math.floor((nw - SIZE) / 2);
//   const top  = Math.floor((nh - SIZE) / 2);

//   const out = document.createElement('canvas');
//   out.width = SIZE;
//   out.height = SIZE;
//   out.getContext('2d').drawImage(tmp, left, top, SIZE, SIZE, 0, 0, SIZE, SIZE);

//   return out.toDataURL('image/jpeg', 0.95).replace(/^data:image\/jpeg;base64,/, '');
// }

async function resizeTo224(canvas) {
  const SIZE = 512;
  const w = canvas.width;
  const h = canvas.height;

  const scale   = SIZE / Math.max(w, h);
  const targetW = Math.round(w * scale);
  const targetH = Math.round(h * scale);

  const bitmap = await createImageBitmap(canvas, {
    resizeWidth:   targetW,
    resizeHeight:  targetH,
    resizeQuality: 'high',
  });

  const out = document.createElement('canvas');
  out.width  = SIZE;
  out.height = SIZE;
  const ctx  = out.getContext('2d');
  const left = Math.floor((SIZE - targetW) / 2);
  const top  = Math.floor((SIZE - targetH) / 2);
  ctx.drawImage(bitmap, left, top);
  bitmap.close();

  return new Promise(resolve => {
    out.toBlob(blob => {
      const reader = new FileReader();
      reader.onload = e => resolve(e.target.result.replace(/^data:image\/\w+;base64,/, ''));
      reader.readAsDataURL(blob);
    }, 'image/png', 0.95);
  });
}


// Resize so the longer side is at most 500px, preserving aspect ratio
function resizeTo500(source) {
  const MAX = 500;
  const sw = source.width  || source.naturalWidth  || source.videoWidth  || MAX;
  const sh = source.height || source.naturalHeight || source.videoHeight || MAX;
  const scale = Math.min(MAX / sw, MAX / sh, 1);
  const nw = Math.max(1, Math.round(sw * scale));
  const nh = Math.max(1, Math.round(sh * scale));
  const c = document.createElement('canvas');
  c.width = nw; c.height = nh;
  c.getContext('2d').drawImage(source, 0, 0, nw, nh);
  return c.toDataURL('image/jpeg', 0.85);
}

function getDeviceInfo() {
  const ua = navigator.userAgent;
  const screen = `${window.screen.width}×${window.screen.height}`;
  const lang = navigator.language || '';
  const threads = navigator.hardwareConcurrency ? `${navigator.hardwareConcurrency} cores` : '';
  return [ua, `screen:${screen}`, lang, threads].filter(Boolean).join(' | ');
}

function makeSessionId() {
  return `psw-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function getOrderImages(result) {
  const items = result.orderData?.lineItems;
  if (!items?.length) return [{ url: result.printFileUrl, mockupUrl: null, name: null, sku: null, variantTitle: null, quantity: 1, vendor: null }];
  return items
    .filter(item => item.printfiles?.length > 0)
    .map(item => ({
      url: item.printfiles[0].imageUrl,
      mockupUrl: item.printfiles[0].mockupUrl,
      name: item.name,
      sku: item.sku,
      variantTitle: item.variantTitle,
      quantity: item.quantity,
      vendor: item.vendor,
    }));
}

function deduplicateResults(results) {
  const seen = new Map();
  const out = [];
  for (const r of results) {
    const key = r.orderData?.id ? String(r.orderData.id) : `${r.client}::${r.orderNumber}`;
    if (!seen.has(key)) { seen.set(key, out.length); out.push(r); }
    else if (r.score > out[seen.get(key)].score) out[seen.get(key)] = { ...out[seen.get(key)], score: r.score };
  }
  return out;
}

// Equal scores → older vectorCreatedAt first
function sortResults(results) {
  return [...results].sort((a, b) => {
    const diff = b.score - a.score;
    if (Math.abs(diff) > 0.00001) return diff;
    return new Date(a.vectorCreatedAt || 0) - new Date(b.vectorCreatedAt || 0);
  });
}

function resultKey(r) {
  return r.driveFileId || `${r.client}::${r.orderNumber}`;
}

const STATUS_LABEL = { 1: 'Pending', 2: 'In Progress', 3: 'Ready', 4: 'Shipped', 5: 'Closed', 6: 'Cancelled' };
const BACKEND_LABELS = { webgpu: '⚡ WebGPU', webgl: '🎮 WebGL', wasm: '🐢 WASM' };
const S3_BASE = 'https://pillow3d-assets.s3.us-west-2.amazonaws.com';
const MODEL_OPTIONS = [
  { value: `${S3_BASE}/model.onnx`,      label: 'model.onnx — FP32 (~104 MB)' },
  { value: `${S3_BASE}/model_int8.onnx`, label: 'model_int8.onnx — INT8 (~27 MB)' },
];
const BACKEND_OPTIONS = [
  { value: 'webgpu', label: 'WebGPU' },
  { value: 'webgl',  label: 'WebGL'  },
  { value: 'auto',   label: 'WASM multi-thread' },
  { value: 'wasm',   label: 'WASM (CPU)' },
];

// ── Station mode ──────────────────────────────────────────────────────────────
// In station mode the camera runs continuously and the whole flow is automated:
// a search fires by itself whenever a pillow appears in frame or the current
// one is swapped for a new one. Manual capture keeps working as an override,
// and printing always stays behind the manual button. Persisted via
// `psw-station-mode`; later this will be driven by a dedicated user role
// instead of a local toggle.
const STATION_SCAN_INTERVAL_MS = 600;  // pause between scan passes (self-scheduling)
const STATION_STABLE_FRAMES    = 2;    // consecutive similar detections before searching
const STATION_MISS_FRAMES      = 3;    // consecutive empty frames before "pillow removed"
const STATION_CHANGE_THRESHOLD = 0.15; // mean abs pixel diff (0-1) that means "different pillow"
const STATION_COOLDOWN_MS      = 2500; // minimum gap between auto-triggered searches
const STATION_MOTION_THRESHOLD = 0.02; // whole-frame mean abs diff (0-1) that counts as motion
const STATION_STILL_FRAMES     = 2;    // consecutive still passes required before detection resumes

// 32×32 grayscale fingerprint of the detected pillow region — a cheap way to
// tell "the same pillow is still lying there" from "a new pillow was placed"
// without re-running the vector search. Same idea as the legacy ImageSearch
// screen's thumbHash.
function hashCanvasRegion(canvas, x, y, w, h) {
  const SIZE = 32;
  const c = document.createElement('canvas');
  c.width = SIZE; c.height = SIZE;
  const ctx = c.getContext('2d');
  ctx.drawImage(canvas, x, y, w, h, 0, 0, SIZE, SIZE);
  const { data } = ctx.getImageData(0, 0, SIZE, SIZE);
  const out = new Float32Array(SIZE * SIZE);
  for (let i = 0; i < out.length; i++) {
    out[i] = (data[i * 4] + data[i * 4 + 1] + data[i * 4 + 2]) / (3 * 255);
  }
  return out;
}

function hashDiff(a, b) {
  let s = 0;
  for (let i = 0; i < a.length; i++) s += Math.abs(a[i] - b[i]);
  return s / a.length;
}

// ── ImageLightbox ─────────────────────────────────────────────────────────────

function ImageLightbox({ src, onClose }) {
  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div className="psw-lightbox" onClick={onClose}>
      <button className="psw-lightbox-close" onClick={onClose}>✕</button>
      <img src={src} alt="" className="psw-lightbox-img" onClick={e => e.stopPropagation()} />
    </div>
  );
}

// ── Order details ─────────────────────────────────────────────────────────────

// General order-level info: status/vendor chips + shipping/created dates.
// Vendor is the same across all line items, so it's shown once here instead
// of being repeated on every item.
function OrderGeneralInfo({ orderData }) {
  if (!orderData) return null;
  const vendor = orderData.lineItems?.find(item => item.vendor)?.vendor;

  return (
    <div className="psw-order-general-info">
      <div className="psw-order-chips">
        <span className={`psw-chip psw-chip--status-${orderData.financialStatus}`}>
          {orderData.financialStatus}
        </span>
        <span className="psw-chip">{STATUS_LABEL[orderData.statusId] || `Status ${orderData.statusId}`}</span>
        <span className="psw-chip">{orderData.itemCount} item{orderData.itemCount !== 1 ? 's' : ''}</span>
        {orderData.onHold && <span className="psw-chip psw-chip--warn">On Hold</span>}
        {vendor && <span className="psw-chip psw-chip--vendor">{vendor}</span>}
      </div>

      <div className="psw-order-info-grid">
        <div className="psw-order-info-block">
          <div className="psw-order-info-label">Shipping</div>
          <div className="psw-order-info-value">{orderData.shippingLineTitle}</div>
          {orderData.shippingLineCode && orderData.shippingLineCode !== orderData.shippingLineTitle && (
            <div className="psw-order-info-code">{orderData.shippingLineCode}</div>
          )}
        </div>

        <div className="psw-order-info-block">
          <div className="psw-order-info-label">Created</div>
          <div className="psw-order-info-value">{new Date(orderData.createdAt).toLocaleString()}</div>
          {orderData.closedAt && (
            <div className="psw-order-info-value psw-order-info-value--muted">Closed: {new Date(orderData.closedAt).toLocaleString()}</div>
          )}
        </div>

        {orderData.note && (
          <div className="psw-order-info-block">
            <div className="psw-order-info-label">Note</div>
            <div className="psw-order-info-value">{orderData.note}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// Line items rendered as a row of (larger) images, each with just its
// distinguishing details — SKU, variant and quantity (vendor lives in
// OrderGeneralInfo since it's identical across items).
function OrderLineItemsRow({ orderData }) {
  if (!orderData?.lineItems?.length) return null;
  return (
    <div className="psw-order-items-row">
      {orderData.lineItems.map(item => (
        <div key={item.id} className="psw-order-item">
          <div className="psw-order-item-img">
            {item.printfiles?.[0]?.mockupUrl
              ? <img src={item.printfiles[0].mockupUrl} alt={item.name} />
              : <div className="psw-order-item-no-img">—</div>
            }
          </div>
          <div className="psw-order-item-info">
            {[
              ['SKU',     item.sku],
              ['Variant', item.variantTitle],
              ['Qty',     item.quantity],
            ].map(([k, v]) => v != null && (
              <div key={k} className="psw-order-item-row">
                <span className="psw-order-item-key">{k}</span>
                <span className="psw-order-item-val">{v}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── SearchResultCard ──────────────────────────────────────────────────────────

function SearchResultCard({
  result, onImageClick, labelPresetId, showBarcodeText,
  sketchEnabled, sketchContrast, sketchShade, removeBackground,
}) {
  const mainImgRef = useRef(null);
  const [printStatus, setPrintStatus] = useState('idle'); // 'idle' | 'printing' | 'error'
  const mainImgUrl = result.printFileUrl;
  const { orderData } = result;

  // All unique printfile images from line items
  const galleryImgs = (() => {
    const items = orderData?.lineItems ?? [];
    const seen = new Set([mainImgUrl]);
    return items
      .flatMap(item => item.printfiles ?? [])
      .map(pf => pf.imageUrl)
      .filter(url => { if (!url || seen.has(url)) return false; seen.add(url); return true; });
  })();

  const scoreVal = Math.round(result.score * 100);

  // One click, straight to the printer: no preview dialog, no extra step.
  // The main photo is already loaded on screen, so we reuse that <img>
  // instead of re-fetching it — printing feels instant.
  const handlePrintLabel = useCallback(() => {
    if (!mainImgUrl || printStatus === 'printing') return;
    setPrintStatus('printing');

    const finishPrint = (image) => {
      const preset = getLabelPreset(labelPresetId);
      const canvas = document.createElement('canvas');
      renderPrintLabel(canvas, {
        image, orderNumber: result.orderNumber, client: result.client,
        preset, showBarcodeText,
        sketchEnabled, sketchContrast, sketchShade, removeBackground,
      });
      printLabelCanvas(canvas, preset);
      setPrintStatus('idle');
    };

    const cached = mainImgRef.current;
    if (cached && cached.complete && cached.naturalWidth > 0) {
      finishPrint(cached);
      return;
    }

    const image = new Image();
    image.onload = () => finishPrint(image);
    image.onerror = () => setPrintStatus('error');
    image.src = mainImgUrl;
  }, [
    mainImgUrl, printStatus, labelPresetId, showBarcodeText,
    sketchEnabled, sketchContrast, sketchShade, removeBackground,
    result.orderNumber, result.client,
  ]);

  // Card is a CSS grid with named areas (header / general / image / items) —
  // desktop keeps the classic image-left / info-right layout, while mobile
  // reflows the very same areas into: header+general info, then the
  // print-ready image, then the line items row at the bottom. See the
  // ".psw-result-card" grid-template-areas rules for each breakpoint.
  return (
    <div className="psw-result-card">
      <div className="psw-result-header-area">
        <div className="psw-result-top">
          <div className="psw-result-heading">
            <span className="psw-result-order">#{result.orderNumber}</span>
            <span className="psw-result-client-badge">{result.client}</span>
          </div>

          <div className="psw-result-score-row">
            <span className="psw-result-score" style={{ color: scoreColor(result.score) }}>
              {scoreVal}%
            </span>
            <span className="psw-result-score-label">similarity</span>
          </div>
        </div>
      </div>

      <div className="psw-result-general-area">
        {orderData ? (
          <OrderGeneralInfo orderData={orderData} />
        ) : (
          <p className="psw-result-no-data">No order data available</p>
        )}
      </div>

      <div className="psw-result-img-col">
        <div className="psw-result-img-wrap" onClick={() => onImageClick(mainImgUrl)}>
          <img ref={mainImgRef} src={mainImgUrl} alt={`#${result.orderNumber}`} className="psw-result-main-img" loading="lazy" />
          <div className="psw-result-img-overlay">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </div>
        </div>

        {galleryImgs.length > 0 && (
          <div className="psw-result-gallery">
            {galleryImgs.map((url, i) => (
              <button key={i} className="psw-result-thumb" onClick={() => onImageClick(url)}>
                <img src={url} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        )}
      </div>

      {orderData && (
        <div className="psw-result-items-area">
          <OrderLineItemsRow orderData={orderData} />
        </div>
      )}

      {mainImgUrl && (
        <button
          type="button"
          className={[
            'psw-btn-print-trigger',
            printStatus === 'printing' && 'psw-btn-print-trigger--busy',
            printStatus === 'error' && 'psw-btn-print-trigger--error',
          ].filter(Boolean).join(' ')}
          onClick={handlePrintLabel}
          disabled={printStatus === 'printing'}
          title={printStatus === 'error' ? 'Could not load the photo — click to retry' : 'Print label'}
        >
          {printStatus === 'printing'
            ? <span className="psw-spinner" />
            : <span className="psw-btn-print-trigger-icon">🖨</span>}
          <span>{printStatus === 'error' ? 'Retry print' : 'Print label'}</span>
        </button>
      )}
    </div>
  );
}

// ── SettingsPanel ─────────────────────────────────────────────────────────────

function SettingsPanel({
  isOpen, onClose,
  selectedModel, setSelectedModel,
  selectedBackend, setSelectedBackend,
  configChanged, applyConfig,
  activeModel,
  modelLoading, modelReady, modelError, backend,
  confThresh, setConfThresh,
  iouThresh, setIouThresh,
  maskThresh, setMaskThresh,
  reRender, hasResult,
  perf, searchTiming,
  outputCanvasRef, maskCanvasRef,
  labelPresetId, setLabelPresetId, showBarcodeText, setShowBarcodeText,
  sketchEnabled, setSketchEnabled, sketchContrast, setSketchContrast,
  sketchShade, setSketchShade, removeBackground, setRemoveBackground,
  stationMode, setStationMode,
}) {
  const isMultiThread = typeof window !== 'undefined' && !!window.crossOriginIsolated;

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    if (isOpen) document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [isOpen, onClose]);

  return (
    <div className={`psw-settings-overlay${isOpen ? ' psw-settings-overlay--open' : ''}`} onClick={onClose}>
      <div className="psw-settings-panel" onClick={e => e.stopPropagation()}>
        <div className="psw-settings-header">
          <span>⚙ Settings</span>
          <button className="psw-icon-btn" onClick={onClose}>✕</button>
        </div>

        <div className="psw-settings-body">

          {/* Thread banner */}
          <div className={`psw-inline-banner ${isMultiThread ? 'psw-inline-banner--ok' : 'psw-inline-banner--warn'}`}>
            {isMultiThread
              ? `✅ Multi-thread · ${navigator.hardwareConcurrency || '?'} cores`
              : '⚠️ Single-thread · add COOP/COEP headers'}
          </div>

          {/* Work mode — station = fully automated camera flow. For now this
              is a local toggle; later it will be driven by a dedicated user
              role. Printing always stays behind the manual button. */}
          <div className="psw-settings-section">
            <div className="psw-settings-section-title">Work Mode</div>
            <label className="psw-settings-toggle">
              <input
                type="checkbox"
                checked={stationMode}
                onChange={e => setStationMode(e.target.checked)}
              />
              <span>Station mode — camera starts by itself and every new pillow in frame is searched automatically</span>
            </label>
          </div>

          {/* Model & backend */}
          <div className="psw-settings-section">
            <div className="psw-settings-section-title">Model &amp; Backend</div>
            <div className="psw-settings-row">
              <select className="psw-select" value={selectedModel} onChange={e => setSelectedModel(e.target.value)}>
                {MODEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select className="psw-select" value={selectedBackend} onChange={e => setSelectedBackend(e.target.value)}>
                {BACKEND_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button
                className={`psw-btn psw-btn--sm ${configChanged ? 'psw-btn--primary' : 'psw-btn--secondary'}`}
                onClick={applyConfig}
              >
                {configChanged ? '🔄 Load' : '✓'}
              </button>
            </div>
            <div className="psw-settings-status">
              {modelLoading && <span style={{ color: '#94a3b8' }}>⏳ Loading {activeModel}…</span>}
              {modelError   && <span style={{ color: '#f87171' }}>❌ {modelError}</span>}
              {modelReady   && (
                <span style={{ color: '#22c55e' }}>
                  ✅ Ready · {BACKEND_LABELS[backend] || backend}
                  {backend === 'wasm' && isMultiThread && ` · ${navigator.hardwareConcurrency || 1} threads`}
                </span>
              )}
            </div>
          </div>

          {/* Detection thresholds */}
          <div className="psw-settings-section">
            <div className="psw-settings-section-title">Detection</div>
            {[
              ['Confidence', confThresh, setConfThresh, 0.05, 0.95],
              ['IoU NMS',    iouThresh,  setIouThresh,  0.10, 0.90],
              ['Mask',       maskThresh, setMaskThresh, 0.10, 0.95],
            ].map(([label, val, setter, min, max]) => (
              <div key={label} className="psw-settings-slider">
                <span className="psw-settings-slider-label">{label}</span>
                <input type="range" min={min} max={max} step="0.05" value={val}
                  onChange={e => setter(parseFloat(e.target.value))} className="psw-slider" />
                <span className="psw-settings-slider-val">{val.toFixed(2)}</span>
              </div>
            ))}
            {hasResult && (
              <button className="psw-btn psw-btn--accent psw-btn--sm" onClick={reRender} style={{ marginTop: 8 }}>
                ↩ Apply mask
              </button>
            )}
          </div>

          {/* Print label — controls the green "🖨 Print label" button on a
              search result. That button prints straight away (no preview
              dialog), using whichever label size / photo-style choice is
              made here — persisted, so it stays the default everywhere. */}
          <div className="psw-settings-section">
            <div className="psw-settings-section-title">Print Label</div>
            <div className="psw-settings-row">
              <select
                className="psw-select"
                value={labelPresetId}
                onChange={e => setLabelPresetId(e.target.value)}
              >
                {LABEL_PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <label className="psw-settings-toggle">
              <input
                type="checkbox"
                checked={showBarcodeText}
                onChange={e => setShowBarcodeText(e.target.checked)}
              />
              <span>Show order # under barcode</span>
            </label>

            {/* Pencil-sketch photo style — same controls as the reference
                "Pencil Barcode Label Maker" tool, since thermal printers are
                monochrome and a sketch reproduces far better than a photo. */}
            <label className="psw-settings-toggle" style={{ marginTop: 10 }}>
              <input
                type="checkbox"
                checked={sketchEnabled}
                onChange={e => setSketchEnabled(e.target.checked)}
              />
              <span>Pencil-sketch photo style</span>
            </label>

            {sketchEnabled && (
              <>
                <div className="psw-settings-slider" style={{ marginTop: 10 }}>
                  <span className="psw-settings-slider-label">Line contrast</span>
                  <input
                    type="range" min={30} max={120} step={1}
                    value={sketchContrast}
                    onChange={e => setSketchContrast(parseInt(e.target.value, 10))}
                    className="psw-slider"
                  />
                  <span className="psw-settings-slider-val">{sketchContrast}</span>
                </div>
                <div className="psw-settings-slider">
                  <span className="psw-settings-slider-label">Shadow softness</span>
                  <input
                    type="range" min={0} max={90} step={1}
                    value={sketchShade}
                    onChange={e => setSketchShade(parseInt(e.target.value, 10))}
                    className="psw-slider"
                  />
                  <span className="psw-settings-slider-val">{sketchShade}</span>
                </div>
                <label className="psw-settings-toggle">
                  <input
                    type="checkbox"
                    checked={removeBackground}
                    onChange={e => setRemoveBackground(e.target.checked)}
                  />
                  <span>Remove plain background</span>
                </label>
              </>
            )}
          </div>

          {/* Model performance */}
          {perf && (
            <div className="psw-settings-section">
              <div className="psw-settings-section-title">Model Performance</div>
              <div className="psw-timing-table">
                {[['Preprocess', perf.preprocess], ['Inference', perf.inference], ['Postprocess', perf.postprocess], ['Render', perf.render]]
                  .map(([n, v]) => (
                    <div key={n} className="psw-timing-row">
                      <span>{n}</span><strong>{fmtMs(v)}</strong>
                    </div>
                  ))}
                <div className="psw-timing-total">
                  Total <strong>{fmtMs(perf.preprocess + perf.inference + perf.postprocess + perf.render)}</strong>
                </div>
              </div>
            </div>
          )}

          {/* API timing */}
          {searchTiming && (
            <div className="psw-settings-section">
              <div className="psw-settings-section-title">API Timing</div>
              <div className="psw-timing-table">
                <div className="psw-timing-row"><span>Request sent</span><strong>{fmtTime(searchTiming.sentAt)}</strong></div>
                <div className="psw-timing-row"><span>Response received</span><strong>{fmtTime(searchTiming.receivedAt)}</strong></div>
                <div className="psw-timing-total">Duration <strong>{fmtMs(searchTiming.durationMs)}</strong></div>
              </div>
            </div>
          )}

          {/* Segmentation canvases */}
          <div className="psw-settings-section">
            <div className="psw-settings-section-title">Segmentation Result</div>
            <div className="psw-settings-canvases">
              <div>
                <div className="psw-canvas-label">Input + bbox</div>
                <canvas ref={outputCanvasRef} className="psw-settings-canvas" />
              </div>
              <div style={{ display: hasResult ? undefined : 'none' }}>
                <div className="psw-canvas-label">Cutout</div>
                <canvas ref={maskCanvasRef} className="psw-settings-canvas psw-canvas--checkerboard" />
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PillowSegmentation({ searchApi, logApi, themeMode = 'dark', userInfo = null }) {
  const sessionId = useRef(makeSessionId());
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Model & backend
  const [selectedModel,   setSelectedModel]   = useState(`${S3_BASE}/model.onnx`);
  const [selectedBackend, setSelectedBackend] = useState('webgpu');
  const [activeModel,     setActiveModel]     = useState(`${S3_BASE}/model.onnx`);
  const [activeBackend,   setActiveBackend]   = useState('webgpu');

  const executionProviders = backendToProviders(activeBackend);
  const { loading: modelLoading, ready: modelReady, error: modelError, backend, runInference,
          phase: modelPhase, progress: modelProgress } =
    useYoloModel({ modelUrl: activeModel, executionProviders });

  const configChanged = selectedModel !== activeModel || selectedBackend !== activeBackend;
  const applyConfig = () => { setActiveModel(selectedModel); setActiveBackend(selectedBackend); };

  // Image
  const fileInputRef    = useRef(null);
  const srcImageRef     = useRef(null);
  const [inputMode,       setInputMode]       = useState('none'); // 'none' | 'upload' | 'camera'
  const [imageLoaded,     setImageLoaded]     = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [imageSize,       setImageSize]       = useState(null);

  // Camera
  const videoRef   = useRef(null);
  const streamRef  = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError,  setCameraError]  = useState(null);

  // Thresholds
  const [confThresh, setConfThresh] = useState(0.25);
  const [iouThresh,  setIouThresh]  = useState(0.45);
  const [maskThresh, setMaskThresh] = useState(0.50);

  // Print label — persisted so the chosen size/settings stick as the
  // page-wide default, whether changed from Settings or from the print dialog.
  const [labelPresetId, setLabelPresetIdState] = useState(
    () => readStoredValue('psw-label-preset-id', DEFAULT_LABEL_PRESET_ID),
  );
  const [showBarcodeText, setShowBarcodeTextState] = useState(
    () => readStoredValue('psw-label-show-text', '1') !== '0',
  );
  const setLabelPresetId = useCallback((id) => {
    setLabelPresetIdState(id);
    writeStoredValue('psw-label-preset-id', id);
  }, []);
  const setShowBarcodeText = useCallback((value) => {
    setShowBarcodeTextState(value);
    writeStoredValue('psw-label-show-text', value ? '1' : '0');
  }, []);
  const [sketchEnabled, setSketchEnabledState] = useState(
    () => readStoredValue('psw-label-sketch-enabled', DEFAULT_SKETCH_ENABLED ? '1' : '0') !== '0',
  );
  const [sketchContrast, setSketchContrastState] = useState(
    () => parseInt(readStoredValue('psw-label-sketch-contrast', String(DEFAULT_SKETCH_CONTRAST)), 10),
  );
  const [sketchShade, setSketchShadeState] = useState(
    () => parseInt(readStoredValue('psw-label-sketch-shade', String(DEFAULT_SKETCH_SHADE)), 10),
  );
  const [removeBackground, setRemoveBackgroundState] = useState(
    () => readStoredValue('psw-label-remove-bg', DEFAULT_REMOVE_BACKGROUND ? '1' : '0') !== '0',
  );
  const setSketchEnabled = useCallback((value) => {
    setSketchEnabledState(value);
    writeStoredValue('psw-label-sketch-enabled', value ? '1' : '0');
  }, []);
  const setSketchContrast = useCallback((value) => {
    setSketchContrastState(value);
    writeStoredValue('psw-label-sketch-contrast', String(value));
  }, []);
  const setSketchShade = useCallback((value) => {
    setSketchShadeState(value);
    writeStoredValue('psw-label-sketch-shade', String(value));
  }, []);
  const setRemoveBackground = useCallback((value) => {
    setRemoveBackgroundState(value);
    writeStoredValue('psw-label-remove-bg', value ? '1' : '0');
  }, []);

  // Station mode — persisted toggle (see constants above)
  const [stationMode, setStationModeState] = useState(
    () => readStoredValue('psw-station-mode', '0') !== '0',
  );
  const setStationMode = useCallback((value) => {
    setStationModeState(value);
    writeStoredValue('psw-station-mode', value ? '1' : '0');
  }, []);
  const [stationStatus, setStationStatus] = useState('waiting'); // 'waiting' | 'motion' | 'stabilizing' | 'tracked'
  const stationFrameRef        = useRef(null); // reused canvas for scan frames
  const stationPendingHashRef  = useRef(null); // candidate pillow being stabilised
  const stationSearchedHashRef = useRef(null); // pillow last sent to search
  const stationStableRef       = useRef(0);
  const stationMissRef         = useRef(0);
  const stationLastSearchRef   = useRef(0);
  const stationPrevFrameHashRef = useRef(null); // whole-frame fingerprint of the previous pass
  const stationStillRef         = useRef(0);    // consecutive passes with no motion in frame
  const analyzingRef           = useRef(false);
  const capturePhotoRef        = useRef(null);

  // Segmentation result (lives in Settings panel canvases)
  const outputCanvasRef = useRef(null);
  const maskCanvasRef   = useRef(null);
  const [isRunning,     setIsRunning]     = useState(false);
  const [runError,      setRunError]      = useState(null);
  const [perf,          setPerf]          = useState(null);
  const [hasResult,     setHasResult]     = useState(false);
  const [detectedCount, setDetectedCount] = useState(0);
  const lastDetectionsRef = useRef(null);
  const lastRenderArgsRef = useRef(null);

  // Vector search
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searchError,   setSearchError]   = useState(null);
  const [searchTiming,  setSearchTiming]  = useState(null);
  const [primaryKey,    setPrimaryKey]    = useState(null);
  const resultsRef = useRef(null);

  // Modals
  const [lightboxSrc,  setLightboxSrc]  = useState(null);

  // Camera helpers
  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setCameraActive(false);
  }, []);

  useEffect(() => {
    if (cameraActive && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [cameraActive]);

  const startCamera = async () => {
    stopCamera();
    setCameraError(null);
    setInputMode('camera');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 } },
      });
      streamRef.current = stream;
      setCameraActive(true);
    } catch (e) {
      setCameraError(`Camera: ${e.message}`);
    }
  };

  const stopCameraMode = () => {
    stopCamera();
    setInputMode('none');
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video?.videoWidth) return;
    const w = video.videoWidth, h = video.videoHeight;
    const cap = document.createElement('canvas');
    cap.width = w; cap.height = h;
    cap.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = cap.toDataURL('image/jpeg', 0.92);
    const img = new Image();
    img.onload = () => {
      srcImageRef.current = img;
      setImageSize(`${w}×${h}`);
      setImagePreviewUrl(dataUrl);
      setImageLoaded(true);
      resetResults();
      // auto-run immediately, camera keeps running in parallel
      runSegmentationWith(img);
    };
    img.src = dataUrl;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const img = new Image();
      img.onload = () => {
        srcImageRef.current = img;
        setImageSize(`${img.width}×${img.height}`);
        setImagePreviewUrl(ev.target.result);
        setImageLoaded(true);
        setInputMode('upload');
        resetResults();
        // auto-run immediately
        runSegmentationWith(img);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const resetResults = () => {
    setHasResult(false); setPerf(null); setRunError(null);
    setSearchResults(null); setSearchError(null); setSearchTiming(null); setDetectedCount(0);
  };

  const resetUpload = () => {
    srcImageRef.current = null;
    setImageLoaded(false); setImagePreviewUrl(null); setImageSize(null);
    setInputMode('none');
    resetResults();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Vector search
  const runVectorSearch = useCallback(async (canvas, { sourceImg, detectedCount: detCnt, perfData } = {}) => {
    if (!searchApi || !canvas) return;
    setSearchLoading(true);
    setSearchError(null);
    const sentAt = new Date();
    let imageB64 = null;
    let results  = [];
    let searchErr = null;
    try {
      imageB64 = await resizeTo224(canvas);
      results  = await searchApi(imageB64);
      const receivedAt = new Date();
      setSearchTiming({ sentAt, receivedAt, durationMs: receivedAt - sentAt });
      setSearchResults(deduplicateResults(results));
      setPrimaryKey(null);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    } catch (e) {
      searchErr = e.message;
      setSearchError(e.message);
    } finally {
      setSearchLoading(false);
    }

    // Fire-and-forget log — never blocks the UI
    if (logApi) {
      const durationMs = Date.now() - sentAt.getTime();
      const logPayload = {
        imageDataUrl: sourceImg ? resizeTo500(sourceImg) : undefined,
        device: getDeviceInfo(),
        logData: {
          sessionId: sessionId.current,
          stationMode,
          user: userInfo ? {
            name:     userInfo.name,
            role:     userInfo.role,
            allRoles: userInfo.allRoles,
          } : null,
          modelVersion: activeModel,
          backend,
          detectedPillows: detCnt ?? 0,
          thresholds: { conf: confThresh, iou: iouThresh, mask: maskThresh },
          perf: perfData ?? null,
          searchDurationMs: durationMs,
          searchImageB64: imageB64,
          results: results.map(r => ({ orderNumber: r.orderNumber, score: r.score, client: r.client })),
          errors: searchErr ? [searchErr] : [],
        },
      };
      logApi(logPayload).catch(() => {/* log errors are silent */});
    }
  }, [searchApi, logApi, userInfo, activeModel, backend, confThresh, iouThresh, maskThresh, stationMode]);

  // Segmentation
  const reRender = () => {
    const img = srcImageRef.current;
    if (!img || !lastDetectionsRef.current || !lastRenderArgsRef.current) return;
    const { scale, ox, oy } = lastRenderArgsRef.current;
    renderToCanvas(lastDetectionsRef.current, img, scale, ox, oy, maskThresh, outputCanvasRef.current, maskCanvasRef.current);
  };

  // Accepts image directly to avoid React state async lag
  const runSegmentationWith = async (img) => {
    if (!img || !modelReady) return;
    setIsRunning(true);
    setRunError(null);
    try {
      const { detections, scale, ox, oy, perf: perfData } = await runInference(img, { confThresh, iouThresh });
      const t3 = performance.now();
      renderToCanvas(detections, img, scale, ox, oy, maskThresh, outputCanvasRef.current, maskCanvasRef.current);
      const renderTime = performance.now() - t3;

      lastDetectionsRef.current = detections;
      lastRenderArgsRef.current = { scale, ox, oy };
      setPerf({ ...perfData, render: renderTime });
      setDetectedCount(detections.length);
      if (detections.length > 0) setHasResult(true);

      const searchCanvas = detections.length > 0
        ? maskCanvasRef.current
        : (() => {
            const fb = document.createElement('canvas');
            fb.width = img.width; fb.height = img.height;
            fb.getContext('2d').drawImage(img, 0, 0);
            return fb;
          })();
      runVectorSearch(searchCanvas, {
        sourceImg: img,
        detectedCount: detections.length,
        perfData: { ...perfData, render: renderTime },
      });
    } catch (e) {
      setRunError(e.message);
      // Log segmentation failure
      if (logApi) {
        logApi({
          imageDataUrl: img ? resizeTo500(img) : undefined,
          device: getDeviceInfo(),
          logData: {
            sessionId: sessionId.current,
            stationMode,
            user: userInfo ? {
              name:     userInfo.name,
              role:     userInfo.role,
              allRoles: userInfo.allRoles,
            } : null,
            modelVersion: activeModel,
            backend,
            detectedPillows: 0,
            thresholds: { conf: confThresh, iou: iouThresh, mask: maskThresh },
            perf: null,
            searchDurationMs: null,
            searchImageB64: null,
            results: [],
            errors: [e.message],
          },
        }).catch(() => {});
      }
    } finally {
      setIsRunning(false);
    }
  };

  const isAnalyzing = isRunning || searchLoading;
  // Latest closures for the station loop — the memoized scan pass must never
  // call a stale capturePhoto / read a stale isAnalyzing from an old render.
  analyzingRef.current = isAnalyzing;
  capturePhotoRef.current = capturePhoto;

  // ── Station mode: automatic capture ────────────────────────────────────────
  // Lightweight scan pass over the live video: decide whether a NEW pillow is
  // sitting in frame. The heavy path (segmentation → vector search → log) is
  // only fired via capturePhoto(), once per pillow.
  const stationScanPass = useCallback(async () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2 || !video.videoWidth) return;

    if (!stationFrameRef.current) stationFrameRef.current = document.createElement('canvas');
    const fc = stationFrameRef.current;
    fc.width = video.videoWidth; fc.height = video.videoHeight;
    fc.getContext('2d').drawImage(video, 0, 0);

    // Motion gate: compare a whole-frame fingerprint against the previous
    // pass. The pillow-region hash below is box-relative, so it looks "stable"
    // even while the pillow is still being slid into frame — this gate is what
    // actually catches that. While anything moves (hands, the pillow itself),
    // skip detection entirely and only resume after the scene has been still
    // for STATION_STILL_FRAMES consecutive passes.
    const frameHash = hashCanvasRegion(fc, 0, 0, fc.width, fc.height);
    const prevFrameHash = stationPrevFrameHashRef.current;
    stationPrevFrameHashRef.current = frameHash;
    if (prevFrameHash && hashDiff(prevFrameHash, frameHash) > STATION_MOTION_THRESHOLD) {
      stationStillRef.current = 0;
      stationStableRef.current = 0;
      stationPendingHashRef.current = null;
      setStationStatus('motion');
      return;
    }
    if (++stationStillRef.current < STATION_STILL_FRAMES) return; // just went still — one more pass to be sure

    const { detections, scale, ox, oy } = await runInference(fc, { confThresh, iouThresh });
    const best = detections[0]; // NMS output is sorted by score

    if (!best) {
      // Tolerate a few empty frames — a hand briefly covering the pillow must
      // not count as "removed", or the same pillow would be searched twice.
      stationStableRef.current = 0;
      stationPendingHashRef.current = null;
      if (++stationMissRef.current >= STATION_MISS_FRAMES) {
        stationSearchedHashRef.current = null;
        setStationStatus('waiting');
      }
      return;
    }
    stationMissRef.current = 0;

    // Model box (512-space) → frame coordinates
    const fw = fc.width, fh = fc.height;
    const [bx1, by1, bx2, by2] = best.box;
    const x1 = Math.max(0, Math.min(fw - 1, (bx1 - ox) / scale));
    const y1 = Math.max(0, Math.min(fh - 1, (by1 - oy) / scale));
    const x2 = Math.max(x1 + 1, Math.min(fw, (bx2 - ox) / scale));
    const y2 = Math.max(y1 + 1, Math.min(fh, (by2 - oy) / scale));
    const hash = hashCanvasRegion(fc, x1, y1, x2 - x1, y2 - y1);

    // Same pillow we already searched → just keep tracking it.
    if (stationSearchedHashRef.current
        && hashDiff(stationSearchedHashRef.current, hash) <= STATION_CHANGE_THRESHOLD) {
      stationStableRef.current = 0;
      stationPendingHashRef.current = null;
      setStationStatus('tracked');
      return;
    }

    // New pillow candidate — wait until it stops moving (N stable frames).
    if (stationPendingHashRef.current
        && hashDiff(stationPendingHashRef.current, hash) <= STATION_CHANGE_THRESHOLD) {
      stationStableRef.current += 1;
    } else {
      stationStableRef.current = 1;
    }
    stationPendingHashRef.current = hash;
    setStationStatus('stabilizing');

    const cooledDown = Date.now() - stationLastSearchRef.current >= STATION_COOLDOWN_MS;
    if (stationStableRef.current >= STATION_STABLE_FRAMES && cooledDown) {
      stationSearchedHashRef.current = hash;
      stationPendingHashRef.current = null;
      stationStableRef.current = 0;
      stationLastSearchRef.current = Date.now();
      capturePhotoRef.current?.(); // full pipeline on a fresh frame
    }
  }, [runInference, confThresh, iouThresh]);

  // Drive the loop with a self-scheduling timeout (not setInterval) so a slow
  // inference pass can never stack up behind itself.
  useEffect(() => {
    if (!stationMode || inputMode !== 'camera' || !cameraActive || !modelReady) return;
    let cancelled = false;
    let timerId = null;
    setStationStatus('waiting');
    const tick = async () => {
      if (cancelled) return;
      if (!analyzingRef.current) {
        try { await stationScanPass(); }
        catch (e) { console.warn('[psw] station scan failed:', e.message); }
      }
      if (!cancelled) timerId = setTimeout(tick, STATION_SCAN_INTERVAL_MS);
    };
    tick();
    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
      stationStableRef.current = 0;
      stationMissRef.current = 0;
      stationPendingHashRef.current = null;
      stationSearchedHashRef.current = null;
      stationPrevFrameHashRef.current = null;
      stationStillRef.current = 0;
    };
  }, [stationMode, inputMode, cameraActive, modelReady, stationScanPass]);

  // Station mode: open the camera by itself once the model is ready, so the
  // operator never has to touch the screen to start a shift. Also brings the
  // camera back after "Back" from upload mode.
  useEffect(() => {
    if (stationMode && modelReady && inputMode === 'none' && !cameraActive) {
      startCamera();
    }
  }, [stationMode, modelReady, inputMode, cameraActive]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`psw-root${themeMode === 'light' ? ' psw-root--light' : ''}`}>

      {/* ── Model loading overlay ── */}
      {(modelLoading || (!modelReady && !modelError)) && (
        <div className="psw-model-overlay">
          <div className="psw-model-overlay-card">
            <div className="psw-model-overlay-spinner" />
            <div className="psw-model-overlay-title">
              {modelPhase === 'init' ? 'Initializing model…' : 'Loading model…'}
            </div>
            {modelPhase === 'download' && modelProgress > 0 && (
              <>
                <div className="psw-model-overlay-bar-wrap">
                  <div
                    className="psw-model-overlay-bar"
                    style={{ width: `${Math.round(modelProgress * 100)}%` }}
                  />
                </div>
                <div className="psw-model-overlay-pct">
                  {Math.round(modelProgress * 100)}%
                </div>
              </>
            )}
            {modelPhase === 'init' && (
              <div className="psw-model-overlay-hint">Connecting to backend…</div>
            )}
            {modelProgress < 0.05 && modelPhase === 'download' && (
              <div className="psw-model-overlay-hint">First load only — will be cached</div>
            )}
          </div>
        </div>
      )}

      {/* Model error state */}
      {modelError && !modelLoading && (
        <div className="psw-model-overlay">
          <div className="psw-model-overlay-card psw-model-overlay-card--error">
            <div className="psw-model-overlay-error-icon">✕</div>
            <div className="psw-model-overlay-title">Failed to load model</div>
            <div className="psw-model-overlay-hint">{modelError}</div>
            <button
              className="psw-btn psw-btn--primary psw-btn--sm"
              style={{ marginTop: 16 }}
              onClick={() => { setActiveModel(m => m.includes('?') ? m.split('?')[0] : m + '?r=' + Date.now()); }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="psw-header">
        <h1 className="psw-title">
          Pillow Search
          {stationMode && <span className="psw-station-badge">⚡ Station</span>}
        </h1>
        <button className="psw-settings-trigger" onClick={() => setSettingsOpen(true)}>
          <span className="psw-settings-trigger-icon">⚙</span>
          Settings
          {!modelReady && <span className="psw-settings-dot psw-settings-dot--warn" />}
          {modelReady  && <span className="psw-settings-dot psw-settings-dot--ok"   />}
        </button>
      </div>

      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />

      {/* Initial: two action cards */}
      {inputMode === 'none' && (
        <div className="psw-actions-grid">
          <button className="psw-action-card" onClick={() => fileInputRef.current?.click()}>
            <div className="psw-action-icon">📁</div>
            <div className="psw-action-label">Upload Photo</div>
            <div className="psw-action-hint">JPEG, PNG, WEBP</div>
          </button>
          <button className="psw-action-card" onClick={startCamera}>
            <div className="psw-action-icon">📷</div>
            <div className="psw-action-label">Camera</div>
            <div className="psw-action-hint">Take a photo</div>
          </button>
        </div>
      )}

      {/* Camera mode — last photo left, live camera right */}
      {inputMode === 'camera' && (
        <div className="psw-camera-wrap">
          {/* Left: last captured photo */}
          {imagePreviewUrl && (
            <div className="psw-camera-last">
              <img src={imagePreviewUrl} alt="Last capture" className="psw-camera-last-img" />
              <div className="psw-camera-last-info">
                {isAnalyzing && (
                  <div className="psw-analyzing">
                    <div className="psw-spinner" />
                    <span>{isRunning ? 'Analyzing…' : 'Searching…'}</span>
                  </div>
                )}
                {!isAnalyzing && searchResults && (
                  <span style={{ color: '#22c55e', fontSize: 13 }}>
                    ✅ {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                  </span>
                )}
                {imageSize && <span className="psw-camera-last-size">{imageSize}</span>}
              </div>
            </div>
          )}

          {/* Right: live camera feed + buttons */}
          <div className="psw-camera-feed">
            {cameraError && <div className="psw-error-msg">{cameraError}</div>}
            {stationMode && !cameraError && (
              <div className={`psw-station-bar psw-station-bar--${isAnalyzing ? 'busy' : stationStatus}`}>
                {isAnalyzing ? (
                  <><div className="psw-spinner" /><span>{isRunning ? 'Analyzing…' : 'Searching…'}</span></>
                ) : stationStatus === 'motion' ? (
                  <span>✋ Motion in frame — waiting for it to settle…</span>
                ) : stationStatus === 'stabilizing' ? (
                  <span>📷 Pillow detected — hold still…</span>
                ) : stationStatus === 'tracked' ? (
                  <span>✅ Done — place the next pillow to search again</span>
                ) : (
                  <span>👁 Waiting for a pillow…</span>
                )}
              </div>
            )}
            <video ref={videoRef} autoPlay playsInline muted className="psw-camera-video" />
            <div className="psw-camera-btns">
              <button className="psw-btn psw-btn--primary psw-btn--lg" onClick={capturePhoto} disabled={isAnalyzing}>
                {isAnalyzing ? <><div className="psw-spinner" /> Processing…</> : '📸 Capture'}
              </button>
              <button className="psw-btn psw-btn--ghost" onClick={stopCameraMode}>✕ Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Upload mode — compact bar with preview + "Upload New One" */}
      {inputMode === 'upload' && (
        <div className="psw-upload-bar">
          <div className="psw-upload-bar-preview">
            <img src={imagePreviewUrl} alt="Preview" className="psw-upload-bar-img" />
            {imageSize && <span className="psw-upload-bar-size">{imageSize}</span>}
          </div>
          <div className="psw-upload-bar-status">
            {isAnalyzing ? (
              <div className="psw-analyzing">
                <div className="psw-spinner psw-spinner--lg" />
                <span>{isRunning ? 'Analyzing image…' : 'Searching database…'}</span>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  className="psw-btn psw-btn--secondary"
                  onClick={() => { resetUpload(); setTimeout(() => fileInputRef.current?.click(), 50); }}
                >
                  📁 Upload New One
                </button>
                <button className="psw-btn psw-btn--ghost" onClick={resetUpload}>
                  ← Back
                </button>
              </div>
            )}
          </div>
          {runError && <div className="psw-error-msg">❌ {runError}</div>}
        </div>
      )}

      {/* Search results */}
      {searchApi && (searchResults !== null || searchError) && (() => {
        const sorted = sortResults(searchResults || []);
        const primKey = primaryKey ?? (sorted[0] ? resultKey(sorted[0]) : null);
        const primaryResult = sorted.find(r => resultKey(r) === primKey) ?? sorted[0];
        const secondaryResults = sorted.filter(r => resultKey(r) !== primKey);
        return (
          <div className="psw-results-section" ref={resultsRef}>
            <div className="psw-results-header">
              <span className="psw-results-title">Best Match</span>
              {searchResults && (
                <span className="psw-results-count">
                  {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                  {detectedCount === 0 && <span className="psw-results-note">· searched by original photo</span>}
                </span>
              )}
            </div>

            {searchError && <div className="psw-error-msg">❌ {searchError}</div>}

            {searchResults?.length === 0 && (
              <div className="psw-results-empty">No similar images found.</div>
            )}

            {primaryResult && (
              <SearchResultCard
                key={resultKey(primaryResult)}
                result={primaryResult}
                onImageClick={src => setLightboxSrc(src)}
                labelPresetId={labelPresetId}
                showBarcodeText={showBarcodeText}
                sketchEnabled={sketchEnabled}
                sketchContrast={sketchContrast}
                sketchShade={sketchShade}
                removeBackground={removeBackground}
              />
            )}

            {secondaryResults.length > 0 && (
              <div className="psw-secondary-section">
                <div className="psw-secondary-label">
                  Other results — click to switch
                </div>
                <div className="psw-secondary-slider">
                  {secondaryResults.map(r => (
                    <button
                      key={resultKey(r)}
                      className="psw-secondary-card"
                      onClick={() => setPrimaryKey(resultKey(r))}
                    >
                      <div className="psw-secondary-img-wrap">
                        <img
                          src={r.printFileUrl || ''}
                          alt={r.orderNumber}
                          loading="lazy"
                        />
                      </div>
                      <div className="psw-secondary-info">
                        <span className="psw-secondary-score">{(r.score * 100).toFixed(1)}%</span>
                        <span className="psw-secondary-order">#{r.orderNumber}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Settings drawer */}
      <SettingsPanel
        isOpen={settingsOpen} onClose={() => setSettingsOpen(false)}
        selectedModel={selectedModel} setSelectedModel={setSelectedModel}
        selectedBackend={selectedBackend} setSelectedBackend={setSelectedBackend}
        configChanged={configChanged} applyConfig={applyConfig}
        activeModel={activeModel}
        modelLoading={modelLoading} modelReady={modelReady} modelError={modelError} backend={backend}
        confThresh={confThresh} setConfThresh={setConfThresh}
        iouThresh={iouThresh} setIouThresh={setIouThresh}
        maskThresh={maskThresh} setMaskThresh={setMaskThresh}
        reRender={reRender} hasResult={hasResult}
        perf={perf} searchTiming={searchTiming}
        outputCanvasRef={outputCanvasRef} maskCanvasRef={maskCanvasRef}
        labelPresetId={labelPresetId} setLabelPresetId={setLabelPresetId}
        showBarcodeText={showBarcodeText} setShowBarcodeText={setShowBarcodeText}
        sketchEnabled={sketchEnabled} setSketchEnabled={setSketchEnabled}
        sketchContrast={sketchContrast} setSketchContrast={setSketchContrast}
        sketchShade={sketchShade} setSketchShade={setSketchShade}
        removeBackground={removeBackground} setRemoveBackground={setRemoveBackground}
        stationMode={stationMode} setStationMode={setStationMode}
      />

      {/* Lightbox */}
      {lightboxSrc && <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </div>
  );
}
